import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20warm%20minimalist%20abstract%20background%20with%20flowing%20organic%20shapes%20in%20gentle%20terracotta%20cream%20and%20warm%20beige%20tones%2C%20subtle%20layered%20geometric%20waves%20with%20delicate%20texture%2C%20clean%20modern%20aesthetic%2C%20soft%20ambient%20diffused%20lighting%2C%20elegant%20editorial%20style%2C%20warm%20neutral%20palette%2C%20ample%20negative%20space%2C%20no%20text%20no%20people%2C%20high%20quality%20sophisticated%20design%20suitable%20for%20premium%20fashion%20ecommerce%20hero&width=1600&height=900&seq=hero-bg-sugargoo-v2&orientation=landscape"
          alt="Sugargoo Spreadsheet background"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
        <div className="mb-4">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-white/15 backdrop-blur-sm text-white rounded-full border border-white/20 whitespace-nowrap">
            Updated Daily &bull; Verified Resources &bull; Free Access
          </span>
        </div>

        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
          Sugargoo Spreadsheet &mdash; The Ultimate Database For Finds, QC Photos &amp; Shopping Guides
        </h1>

        <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
          Discover thousands of curated Sugargoo Spreadsheet finds, product recommendations, QC insights, shipping guides and expert reviews in one place.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-6 py-3 bg-white text-foreground-800 rounded-full text-sm font-medium hover:bg-accent-50 transition-all duration-200 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line"></i>
              Browse Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-xl overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                <div className="max-h-72 overflow-y-auto py-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors duration-150 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 shadow-lg whitespace-nowrap"
          >
            Explore Sugargoo Spreadsheet
            <i className="ri-arrow-right-line"></i>
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-200 shadow-lg whitespace-nowrap"
          >
            Buy It Now
            <i className="ri-shopping-bag-line"></i>
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-5">
          {[
            { icon: 'ri-checkbox-circle-line', text: 'Updated Daily' },
            { icon: 'ri-shield-check-line', text: 'Verified Resources' },
            { icon: 'ri-guide-line', text: 'Complete Buying Guides' },
            { icon: 'ri-lock-unlock-line', text: 'Free Access' },
          ].map((badge) => (
            <span
              key={badge.text}
              className="flex items-center gap-1.5 text-xs md:text-sm text-white/70 whitespace-nowrap"
            >
              <i className={badge.icon}></i>
              {badge.text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}